<template>
  <section>
    <VSuggestionSelectV2
      v-model="option.seller"
      :data="sellerSource"
      :type="Partner"
      layout="vertical"
      label="Поставщик"
      required
    >
      <template #helper-text>
        <template v-if="isNewSeller">
          Поставщик не найден и будет создан автоматически (страна {{ newSellerCountryName }})
        </template>
        <template v-else>
          Поставщик ID: {{ option.seller?.id || 'отсутствует' }}
        </template>
      </template>
    </VSuggestionSelectV2>

    <VSuggestionSelectV2
      v-model="option.manufacturer"
      :data="manufacturerSource"
      :type="Manufacturer"
      label="Производитель"
      layout="vertical"
      required
    >
      <template #helper-text>
        <template v-if="isNewManufacturer">
          Производитель не найден и будет создан автоматически
        </template>
        <template v-else>
          Производитель ID: {{ option.manufacturer?.id || 'отсутствует' }}
        </template>
      </template>
    </VSuggestionSelectV2>

    <VInput
      v-model="option.sellersCode"
      label="Артикул поставщика"
      layout="vertical"
    />

    <VInput
      v-model="option.nameOrigin"
      label="Наименование товара поставщика"
      layout="vertical"
      required
    />
  </section>

  <hr>

  <section>
    <header>Количество и цена</header>
    <VFormItem class="qty-block">
      <VInputNumberRusV2
        v-model="option.qtyAvailable"
        class="qty"
        :type="Qty"
        label="Количество"
        layout="vertical"
        :max="9999999999"
        :min="0.001"
        required
      />

      <VSelectV2
        v-model="option.unit"
        class="unit"
        layout="vertical"
        label="Ед. изм"
        :data="$core.dictionaries.units"
      />

      <template #helper-text>
        <VHelperText
          theme="blue-flat"
        >
          Необходимое количество {{ option.demand.qtyDesired?.format(option.demand.unit) || '--' }}
        </VHelperText>
      </template>
    </VFormItem>

    <VFormItem
      class="price"
      label="Цена поставщика"
      with-asterisk
      layout="vertical"
      required
    >
      <VInputNumberRusV2
        v-model="option.priceSeller"
        required
        layout="vertical"
        class="price-control"
        :type="Price"
        :max="99999999999999"
        :min="0.001"
      />

      <VSelectV2
        v-model="offerCurrency"
        required
        hide-clear-btn
        class="currency-control"
        :data="currencySource"
      />

      <span class="unit">
        за {{ option.demand.unit?.name || '--' }}
      </span>

      <template #helper-text>
        НДС:
        <VButton
          theme="link"
          @click="showVATPopup"
        >
          {{ VATStringRepresentation }}
        </VButton>
      </template>
    </VFormItem>

    <VFormItem
      class="price-full"
      label="Полная цена поставщика"
      layout="vertical"
      required
    >
      <VInputNumberRusV2
        v-model="option.priceFullSeller"
        disabled
        layout="vertical"
        :type="Price"
        :max="99999999999999"
        :min="0.001"
        required
      />

      <span class="unit">
        за {{ option.demand.unit?.name || '--' }}
      </span>
    </VFormItem>
  </section>

  <hr>

  <section>
    <header>Доставка</header>
    <VSelectV2
      v-model="option.deliveryType"
      label="Тип доставки"
      layout="vertical"
      required
      :data="DeliveryTypesService.getListForOrderPosition(false)"
    />

    <VInputNumberRus
      ref="deliveryDaysInput"
      v-model="option.deliveryDays"
      label-width="220px"
      label="Срок доставки до покупателя"
      :precision="0"
      layout="vertical"
      :min="0"
      :max="365"
      required
    >
      <template
        v-if="option.isDeliveryNeeded"
        #helper-text
      >
        Доставкой до покупателя занимается Платформа.
      </template>
    </VInputNumberRus>

    <VSuggestionSelectV2
      v-model="option.sellerStore"
      class="store-select"
      label="Склад"
      label-width="220px"
      layout="vertical"
      :data="sellerStoreSource"
      :type="Store"
    >
      <template #helper-text>
        <template v-if="isNewStore">
          Склад поставщика не найден и будет создан автоматически
        </template>
        <template v-else>
          Склад: {{ option.sellerStore?.address || 'отсутствует' }}
        </template>
      </template>
    </VSuggestionSelectV2>
  </section>

  <hr>

  <section>
    <VSuggestionSelectV2
      v-model="option.sellerCompany"
      label="Юр. лицо"
      layout="vertical"
      :required="isNewSeller === false"
      :disabled="isNewSeller"
      :data="sellerCompanySource"
      :type="Company"
    >
      <template #helper-text>
        <template v-if="isNewSeller">
          Юр. лицо будет создано автоматически вместе с поставщиком
        </template>
        <template v-else-if="isNewSellerCompany">
          Юр. лицо не найдено и будет создано автоматически
        </template>
        <template v-else>
          Юр. лицо ID: {{ option.sellerCompany?.id || 'отсутствует' }}
        </template>
      </template>
    </VSuggestionSelectV2>
  </section>

  <VPopup
    class="demand-option-vat-popup"
    :target="VATPopup.target"
    :visible="VATPopupVisible"
    padding="10px"
    show-pointer
    @update:visible="VATPopupClose"
  >
    <VFormItem
      label="НДС"
      layout="vertical"
    >
      <VSelectV2
        v-model="option.vatRateSeller"
        class="VAT-size-control"
        hide-clear-btn
        :data="$core.dictionaries.vatRates"
      />

      <VSelectV2
        v-model="localOptionIsVatIncludedSeller"
        class="include-VAT-control"
        hide-clear-btn
        placeholder="--"
        :data="$core.dictionaries.vatInclusionOptions"
      />
    </VFormItem>
  </VPopup>
</template>

<script lang="ts">

import DemandOption         from '@/domains/DemandsV2/DemandOption';
import Store                from '@/domains/Stores/Store';
import DeliveryTypesService from '@/domains/DeliveryTypes/DeliveryTypesService';
import PartnersService      from '@/domains/Partners/PartnersService';
import ManufacturersService from '@/domains/Manufacturers/ManufacturersService';
import CompaniesService     from '@/domains/Companies/CompaniesService';
import StoresService        from '@/domains/Stores/StoresService';

import Partner              from '@/domains/Partners/Partner';
import Manufacturer         from '@/domains/Manufacturers/Manufacturer';
import Company              from '@/domains/Companies/Company';

import Qty                  from '@/core/types/Qty';
import Price                from '@/core/types/Price';

import {UnwrapNestedRefs} from "vue";

export default defineComponent({
  name: 'DemandOptionPrimaryTab',

  props: {
    option: {
      type:     Object as PropType<DemandOption | UnwrapNestedRefs<DemandOption>>,
      required: true,
    },
  },

  setup() {
    return {
      Qty,
      Price,
      /** Типы доставки берём прямо из сервиса: предикат «всё кроме byETP» —
       *  это getListForOrderPosition для позиции не на основе предложения,
       *  повторять его в компоненте незачем. Доставку byETP оформляет
       *  платформа, вариант поставщика её не предлагает. */
      DeliveryTypesService,

      /** Классы сущностей для свободного ввода в VSuggestionSelectV2. */
      Partner,
      Manufacturer,
      Company,
      Store,
    };
  },

  data() {
    /**
     * Правило прелоада зависимых источников: новый вариант — грузим при
     * маунте, существующий — по фокусу, шанс, что реквизиты станут менять,
     * низок. Признак из пропа: вариант приходит снаружи и в data() уже есть.
     */
    const isNewDocument = this.$core.utils.isNullOrUndefined(this.option?.id);

    return {
      VATPopup: {
        target: null as HTMLElement | null,
      },

      /** Поиск по вводу: сущности, которых нет в базе, создаются на сохранении
       *  варианта — см. проп :type у контролов. */
      sellerSource: this.$core.loader.select(({ pattern }) => PartnersService.searchSellers(pattern)),

      manufacturerSource: this.$core.loader.select(({ pattern }) => ManufacturersService.search(pattern)),

      /**
       * Склады и юрлица поставщика грузятся запросом по выбранному партнёру.
       *
       * Не через seller.stores / seller.companies, как в реквизитах сборки:
       * там поставщика всегда выбирают через поиск, и он приходит с
       * коллекциями. Здесь вариант приходит с сервера с усечённым партнёром,
       * а наполнявший его option.fetchDetails() убран — он тянул партнёра
       * целиком ради двух коллекций и затирал _seller на каждом открытии.
       *
       * transportZoneId не передаём: у варианта потребности зоны нет, бэк в
       * этом случае вернёт все склады поставщика.
       */
      sellerStoreSource: this.$core.loader.select(({ seller }) => this.loadSellerStores(seller))
        .dependsOn({ seller: () => this.option.seller })
        .preloadOnMount(isNewDocument === true)
        .preloadOnFocus(isNewDocument === false),

      sellerCompanySource: this.$core.loader.select(({ seller }) => this.loadSellerCompanies(seller))
        .dependsOn({ seller: () => this.option.seller })
        .default((options) => options[0])
        .preloadOnMount(isNewDocument === true)
        .preloadOnFocus(isNewDocument === false),

      /** Валюты — единственный случай, где обёртка нужна: опознавать опции
       *  надо по code, а identity живёт в источнике. */
      currencySource: this.$core.loader.select(Array.from(this.$core.currencies.currencies.values()))
        .setIdentity('code'),
    };
  },

  computed: {
    VATPopupVisible(): boolean {
      return Boolean(this.VATPopup.target);
    },


    /**
     * Признак «НДС включён». В модели boolean, в справочнике объект
     * { value, name } — отсюда переходник: VSelectV2 отдаёт наружу элемент
     * списка, а не его поле. У ставки такого переходника нет: vatRateSeller
     * типизирован VatRate, и элемент справочника vatRates — тот же VatRate,
     * так что селект биндится на поле напрямую.
     *
     * Поиск делаем здесь, а не через DemandOption.selectedIsVatRateSellerOption:
     * тот помимо поиска подменял результат заглушкой { id: 0, name: '--' } —
     * объектом, которого в справочнике нет, да ещё и по состоянию соседнего
     * поля vatRateSeller. Прочерк при незаполненных данных — забота контрола.
     */
    localOptionIsVatIncludedSeller: {
      set(option: { value: boolean } | null): void {
        this.option.isVatIncludedSeller = option?.value ?? null;
      },
      get(): { value: boolean; name: string } | undefined {
        return this.$core.dictionaries.vatInclusionOptions
          .find((item: { value: boolean }) => item.value === this.option.isVatIncludedSeller);
      },
    },

    VATStringRepresentation(): string {
      if (this.option.sellerCompany?.isVatPayer === false) {
        return 'Поставщик не платит';
      }
      else if (
        typeof this.option.isVatIncludedSeller !== 'boolean'
        || !this.option.vatRateSeller
      ) {
        return '--';
      }
      else {
        /** Ставку по справочнику не ищем: vatRateSeller — сам VatRate, а его
         *  name («20%») и есть представление. Прежний поиск сравнивал item.value
         *  с объектом ставки, ничего не находил и печатал undefined. */
        const isVatIncludedStringRepresentation = this.$core.dictionaries.vatInclusionOptions
          .find((item: any) => item.value === this.option.isVatIncludedSeller)?.name;

        return `${this.option.vatRateSeller.name} ${isVatIncludedStringRepresentation}`;
      }
    },

    /**
     * Сущность введена, но в базе её ещё нет — при сохранении будет создана
     * неявно. Пустое поле даёт null, и строгое сравнение с false его отсекает:
     * у отсутствующей сущности isExisting не undefined, а самого объекта нет.
     */
    isNewSeller(): boolean {
      return this.option.seller?.isExisting === false;
    },

    /** Страна нового поставщика — её проставляет Partner.fromSuggestion. */
    newSellerCountryName(): string {
      return this.option.seller?.country?.name || '--';
    },

    isNewSellerCompany(): boolean {
      return this.option.sellerCompany?.isExisting === false;
    },

    isNewManufacturer(): boolean {
      return this.option.manufacturer?.isExisting === false;
    },

    isNewStore(): boolean {
      return this.option.sellerStore?.isExisting === false;
    },

    offerCurrency: {
      set(currency: any): void {
        this.option.offerCurrencyCode = currency.code;
      },
      get(): any {
        return this.$core.utils.money.getCurrencyByCode(this.option.offerCurrencyCode);
      },
    },
  },

  methods: {
    /**
     * Склады поставщика. У введённого вручную партнёра id нет, и запрос ушёл
     * бы с partnerId: undefined — бэк вернул бы склады всех партнёров.
     * dependsOn от этого не спасает: он проверяет непустоту зависимости, а
     * новый партнёр не пуст, у него просто нет id.
     */
    loadSellerStores(seller: Partner): Promise<Store[]> | Store[] {
      if (this.$core.utils.isNullOrUndefined(seller?.id)) {
        return [];
      }
      else {
        return StoresService.getList({ partnerId: seller.id });
      }
    },

    /** Юрлица поставщика. Условие то же, что у складов. */
    loadSellerCompanies(seller: Partner): Promise<Company[]> | Company[] {
      if (this.$core.utils.isNullOrUndefined(seller?.id)) {
        return [];
      }
      else {
        return CompaniesService.getList({ partnerId: seller.id });
      }
    },

    showVATPopup(event: MouseEvent): void {
      this.VATPopup.target = event.target as HTMLElement;
    },

    VATPopupClose(): void {
      this.VATPopup.target = null;
    },
  },
});
</script>
